// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import { memo, useState } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';

import { fontSize, spacing, mediaMax } from 'presentations/views/styles';
import { gray01, gray02 } from 'presentations/views/styles/color';
import Icon from 'presentations/views/components/atoms/Icon';
import Link from 'presentations/views/components/atoms/Link';
import Image from 'presentations/views/components/atoms/Image';
import EventLocationTypeListDialog from 'presentations/views/components/organisms/EventLocationTypeListDialog';
import { getLocations } from 'presentations/views/helpers/event_type';
import {
  EventTypeLocationItem,
  EventTypeLocationListItem,
} from 'presentations/api/schema/event_type_location';
import { LocationType } from 'domains/models/event_type/locationType';
import { helpPath } from 'presentations/routes/path';

type Props = {
  duration: number | null;
  location: EventTypeLocationItem | EventTypeLocationListItem | null;
  isShowGuest?: boolean;
  isRequest?: boolean;
};

const Component = memo((props: Props) => {
  const [
    isOpenEventLocationTypeListDialog,
    setOpenEventLocationTypeListDialog,
  ] = useState(false);

  const t = translateInComponent(translations);
  const locations = getLocations(props.location) || [];
  const suffix =
    locations.length >= 2 ? t('suffix', { count: locations.length - 1 }) : null;
  let locationTypes = locations.map((e) => e.id);
  if (props.location && 'sort' in props.location) {
    const sort = props.location.sort?.split(',') ?? [];
    locationTypes = sort.map((e) => Number(e));
  }
  let location = null;
  if (locationTypes.length > 0) {
    location = locations.find((e) => e.id === locationTypes[0]);
  }
  if (!location && locations.length > 0) {
    location = locations[0];
  }

  const formatLocationName = (location: any) => {
    if (!location) return '';

    if (props.isShowGuest) {
      if ([LocationType.InPerson, LocationType.Custom].includes(location.id)) {
        return location.text || location.name;
      }
    }
    return location.name;
  };

  return (
    <>
      <MetaList>
        {props.isRequest ? (
          <MetaListItem noWrap>
            <MetaListItemIcon name="tag-check-outline" size={fontSize(20)} />
            <MetaListItemText>{t('request system')}</MetaListItemText>
            <StyledLink
              href={helpPath(props.isShowGuest ? '8293281' : '8152919')}
              target="_blank"
            >
              <StyledIcon name="help" size={fontSize(16)} />
            </StyledLink>
          </MetaListItem>
        ) : null}
        {props.duration !== null && (
          <MetaListItem noWrap>
            <MetaListItemIcon name="schedule" size={fontSize(20)} />
            <MetaListItemText>
              {t('duration', { minutes: props.duration })}
            </MetaListItemText>
          </MetaListItem>
        )}
        {location && (
          <MetaListItem key={location.id}>
            <MetaListItemImage src={location.image} alt={location.name || ''} />
            <MetaListItemText>
              <StyledLocationName>
                {formatLocationName(location)}
              </StyledLocationName>
              {suffix && (
                <Suffix
                  onClick={() => setOpenEventLocationTypeListDialog(true)}
                >
                  {suffix}
                </Suffix>
              )}
            </MetaListItemText>
          </MetaListItem>
        )}
      </MetaList>
      <EventLocationTypeListDialog
        isOpen={isOpenEventLocationTypeListDialog}
        locations={locations}
        onClickClose={() => setOpenEventLocationTypeListDialog(false)}
      />
    </>
  );
});

export default Component;

const MetaList = styled.div`
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  ${mediaMax.tablet} {
    align-items: baseline;
  }
  ${mediaMax.smartphone} {
    flex-direction: column;
  }
`;
const MetaListItem = styled.div<{ noWrap?: boolean }>`
  margin-right: ${spacing(12)};
  color: ${gray01};
  display: flex;
  align-items: flex-start;
  font-size: ${fontSize(13)};
  :last-of-type {
    margin-right: 0;
  }
  ${mediaMax.smartphone} {
    margin-right: 0;
    margin-bottom: ${spacing(8)};
    :last-of-type {
      margin-bottom: 0;
    }
  }
  ${({ noWrap }) =>
    noWrap &&
    css`
      white-space: nowrap;
    `}
`;
const Suffix = styled.span`
  display: inline-block;
  font-size: ${fontSize(12)};
  text-decoration: underline;
  cursor: pointer;
`;
const MetaListItemIcon = styled(Icon)`
  font-size: ${fontSize(20)};
  margin-right: ${spacing(4)};
`;
const MetaListItemImage = styled(Image)`
  width: ${fontSize(20)};
  margin-right: ${spacing(4)};
`;
const MetaListItemText = styled.span``;
const StyledLocationName = styled.span`
  display: inline-block;
  margin-right: ${spacing(4)};
  word-break: break-all;
`;
const StyledLink = styled(Link)`
  margin-left: ${spacing(4)};
`;
const StyledIcon = styled(Icon)`
  width: 18px !important;
  height: 18px !important;
  color: ${gray02};
  ${mediaMax.tablet} {
    width: 18px !important;
    height: 18px !important;
  }
`;
